import { Link } from "react-router-dom";
import { Gift, Share2, FileSignature, HandCoins, ArrowRight, Infinity as InfinityIcon, ShieldCheck, Users } from "lucide-react";
import { Reveal } from "@/components/Reveal";

export const REFERRAL_REWARD = 500;

const STEPS = [
  {
    icon: Share2,
    title: "Share your link",
    text: "Every CompCar client gets a personal referral link in the client portal. Copy it and send it to anyone who needs a car — WhatsApp, Facebook, wherever.",
  },
  {
    icon: FileSignature,
    title: "Your friend applies and signs",
    text: "They pre-qualify, complete the full application, and sign their rent-to-own agreement. Your code is attached automatically from your link.",
  },
  {
    icon: HandCoins,
    title: `You earn R${REFERRAL_REWARD}`,
    text: "The moment their agreement is signed, you earn R500 — credited straight off your ownership balance, or paid in cash if your plan is already settled.",
  },
];

const TERMS = [
  { icon: InfinityIcon, text: "Unlimited referrals — every friend who signs earns you another reward." },
  { icon: ShieldCheck, text: "Reward is earned on signature of the referred client's consumer agreement, after verification and approval." },
  { icon: Users, text: "Friends must apply through your personal link (or quote your code) so the referral is recorded." },
];

export default function ReferAFriend() {
  return (
    <div data-testid="refer-a-friend-page" className="pt-32 pb-24 min-h-screen">
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="max-w-3xl mb-16">
            <p className="text-xs uppercase tracking-[0.3em] text-sky-300 font-bold mb-4">Referral programme</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight font-black text-white mb-6">
              Put a friend on the road. <span className="text-[#F57C00]">Pocket R{REFERRAL_REWARD}.</span>
            </h1>
            <p className="text-base text-slate-400 leading-relaxed">
              You know someone who's tired of unreliable taxis and rejected finance applications.
              Send them your CompCar link — when they sign, we thank you with R{REFERRAL_REWARD} off
              your ownership balance (or cash in hand if you're already settled).
            </p>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {STEPS.map((s, i) => (
            <Reveal key={s.title} delay={0.1 + i * 0.1}>
              <div className="bg-[#122659]/80 backdrop-blur-xl border border-sky-300/20 rounded-xl p-8 h-full" data-testid={`referral-step-${i + 1}`}>
                <span className="font-display text-5xl font-black text-[#F57C00]/20">{String(i + 1).padStart(2, "0")}</span>
                <s.icon size={26} className="text-[#F57C00] mt-4 mb-4" />
                <h2 className="font-display text-lg font-black text-white mb-3">{s.title}</h2>
                <p className="text-sm text-slate-400 leading-relaxed">{s.text}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.2}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
            <div className="rounded-2xl border border-[#F57C00]/30 bg-gradient-to-br from-[#F57C00]/15 via-[#122659] to-[#122659] p-8 md:p-10 flex flex-col justify-between" data-testid="referral-cta-card">
              <div>
                <Gift size={28} className="text-[#F57C00] mb-5" />
                <h2 className="font-display text-2xl sm:text-3xl font-black text-white mb-3">Already a CompCar client?</h2>
                <p className="text-sm text-slate-300 leading-relaxed mb-8">
                  Your personal referral link is waiting in your client portal — copy it and start sharing.
                  You can watch your referrals and signed friends there too.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/portal" data-testid="referral-portal-btn" className="flex-1 inline-flex items-center justify-center gap-2 bg-[#F57C00] hover:bg-[#E67300] text-white font-bold px-6 py-4 rounded-md transition-colors duration-300">
                  Get my referral link <ArrowRight size={16} />
                </Link>
              </div>
            </div>

            <div className="bg-[#122659]/80 border border-sky-300/20 rounded-xl p-8 md:p-10" data-testid="referral-terms-card">
              <h2 className="font-display text-xl font-black text-white mb-6">The fine print — kept simple</h2>
              <ul className="space-y-5">
                {TERMS.map((t) => (
                  <li key={t.text} className="flex items-start gap-3 text-sm text-slate-300">
                    <t.icon size={18} className="text-sky-300 mt-0.5 shrink-0" />
                    {t.text}
                  </li>
                ))}
              </ul>
              <p className="text-[11px] text-slate-500 leading-relaxed mt-8 pt-6 border-t border-sky-300/10">
                Each month our most active referrer is crowned Referrer of the Month — with a bonus thank-you
                on top of the standard reward. Not a client yet? <Link to="/pre-qualify" className="text-sky-300 hover:text-white">Pre-qualify in two minutes</Link> —
                once your application is in, your own referral link is created automatically.
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
